import { Modal, Space } from 'antd';
import { MessageTypeEnum, useGetFileMessageQuery, useGetOneMessageQuery } from 'api/MessagesApi';
import DownloadLink from 'components/Message/DownloadLink/DownloadLink';
import MessageIcon from 'components/Message/MessageIcon';
import SpinComponent from 'components/Tool/SpinComponent/SpinComponent';
import { AppAudioPlayer } from 'components/UiKit/Media/AppAudioPlayer';
import { AppVideoPlayer } from 'components/UiKit/Media/AppVideoPlayer';
import { TextToHtmlComponent } from 'components/UiKit/TextToHtmlComponent/TextToHtmlComponent';
import { useCompanyContext } from 'contexts/CompanyContext';
import { useSelector } from 'react-redux';
import { closeModal, selectModalData, selectModalName } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import { isAudioFile, isVideoFile } from 'utils/utils';
import './PreviewMessageModalComponent.css';

export const PREVIEW_MESSAGE_MODAL_NAME = 'PREVIEW_MESSAGE_MODAL_NAME';

type AllProps = {
  messageId: string;
};

function PreviewMessageModalComponent({ messageId }: AllProps) {
  const { companyConfig } = useCompanyContext();
  const dispatch = useAppDispatch();

  const { data: message, isLoading } = useGetOneMessageQuery({ id: messageId });
  const { data: messageFile, isLoading: isLoadingFile } = useGetFileMessageQuery({ id: messageId });

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  return (
    <Modal
      open={true}
      title="Preview File"
      onOk={handleCloseModal}
      onCancel={handleCloseModal}
      okText="Close"
      cancelButtonProps={{ style: { display: 'none' } }}
    >
      <SpinComponent isLoading={isLoading || isLoadingFile}>
        <div className="preview-message-modal-component">
          {message && (
            <>
              <p>
                Message title: <strong>{message.name}</strong>
              </p>

              <div className="message-content">
                {message.fileName ? (
                  (() => {
                    const fallback = (
                      <Space size="small">
                        <MessageIcon type={message.type} />
                        <DownloadLink
                          message={message}
                          messageFile={messageFile}
                          designProps={companyConfig}
                          text={message.fileName}
                        />
                      </Space>
                    );

                    if (message.type === MessageTypeEnum.Video && isVideoFile(message.fileName)) {
                      return (
                        <AppVideoPlayer url={messageFile?.signedUrl} fileName={message.fileName} fallback={fallback} />
                      );
                    }

                    if (message.type === MessageTypeEnum.Audio && isAudioFile(message.fileName)) {
                      return (
                        <AppAudioPlayer url={messageFile?.signedUrl} fileName={message.fileName} fallback={fallback} />
                      );
                    }

                    return fallback;
                  })()
                ) : message.type === MessageTypeEnum.Text ? (
                  <TextToHtmlComponent text={message.content} />
                ) : null}
              </div>

              <p>
                Click{' '}
                <DownloadLink message={message} messageFile={messageFile} designProps={companyConfig} text="here" /> to
                download
              </p>
            </>
          )}
        </div>
      </SpinComponent>
    </Modal>
  );
}

function PreviewMessageModalComponentWrapper() {
  const modalName = useSelector(selectModalName);
  const modalData = useSelector(selectModalData);
  const messageId = modalData?.id || null;

  return modalName === PREVIEW_MESSAGE_MODAL_NAME ? <PreviewMessageModalComponent messageId={messageId} /> : null;
}

export default PreviewMessageModalComponentWrapper;
